import { Table, TableBody, TableCell, TableHeader, TableRow } from "./index";
import clsx from "clsx";
import { DynamicTableProps, TableAction } from "types/globalTypes";
import { useTranslation } from "react-i18next";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import Checkbox from "components/input/Checkbox";
import TooltipHorizon from "components/tooltip";

export default function DynamicTable<T>({
  columns,
  data,
  actions = [],
  loading = false,
  selectable = false,
  getRowId,
  isSelected,
  onSelect,
  pagination,
}: DynamicTableProps<T>) {
  const { t } = useTranslation();

  const getActionIcon = (action: TableAction<T>) => {
    if (action.icon) return action.icon;
    if (action.see) return <FaEye size={14} />;
    if (action.edit) return <FaEdit size={14} />;
    if (action.delete) return <FaTrash size={14} />;

    return null;
  };

  const getButtonClasses = (color?: string) => {
    switch (color) {
      case "success":
        return "bg-green-700 hover:bg-green-800 text-white";
      case "error":
        return "bg-red-500 hover:bg-red-600 text-white";
      case "warning":
        return "bg-yellow-400 hover:bg-yellow-500 text-white";
      case "secondary":
        return "bg-indigo-700 hover:bg-indigo-800 text-white";
      case "primary":
      default:
        return "bg-blue-500 hover:bg-blue-600 text-white";
    }
  };

  const isActionHidden = (action: TableAction<T>, row: T): boolean => {
    if (typeof action.hidden === "function") {
      return action.hidden(row);
    }
    return action.hidden || false;
  };

  return (
    <div className="relative mt-5 min-h-[100px] rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-gray-500/30">
          <div className="loader w-6 text-blue-700" />
        </div>
      )}
      <div className="w-full overflow-x-auto">
        <div
          className="min-h-[100px] min-w-full"
          style={{ width: "max-content", minWidth: "100%" }}
        >
          <Table
            className={clsx({ "pointer-events-none opacity-50": loading })}
          >
            {/* Table Header */}
            <TableHeader className="border-b border-gray-100 bg-indigo-600 dark:border-white/[0.05] dark:bg-navy-700">
              <TableRow className="text-white">
                {selectable && getRowId && (
                  <TableCell
                    isHeader
                    className="text-theme-xs px-5 py-3 text-center font-medium"
                  >
                    {t("table.select")}
                  </TableCell>
                )}
                <TableCell
                  isHeader
                  className="text-theme-xs px-5 py-3 text-center font-medium"
                >
                  {t("table.row")}
                </TableCell>
                {columns.map((col) => (
                  <TableCell
                    key={col.key as string}
                    isHeader
                    className="text-theme-xs px-5 py-3 text-center font-medium"
                  >
                    {col.title}
                  </TableCell>
                ))}
                {actions.length > 0 && (
                  <TableCell
                    isHeader
                    className="text-theme-xs px-2 py-3 font-medium"
                  >
                    {t("table.action")}
                  </TableCell>
                )}
              </TableRow>
            </TableHeader>

            {/* Table Body */}
            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {data.length === 0 ? (
                <TableRow className="text-gray-700 dark:text-gray-400">
                  <TableCell className="flex items-center justify-center truncate px-4 py-3">
                    {t("table.noData")}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, rowIndex) => {
                  const actualIndex = pagination
                    ? (pagination.page - 1) * pagination.limit + rowIndex
                    : rowIndex;
                  return (
                    <TableRow
                      key={rowIndex}
                      className={clsx(
                        "text-gray-700 transition-colors dark:text-gray-400",
                        actualIndex % 2 === 0
                          ? "bg-white hover:bg-gray-50/80 dark:bg-white/[0.02] dark:hover:bg-white/[0.05]"
                          : "bg-gray-200/60 hover:bg-gray-200/80 dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
                      )}
                    >
                      {selectable && getRowId && (
                        <TableCell className="flex items-center justify-center px-4 py-3">
                          <Checkbox
                            checked={!!isSelected?.(getRowId(row))}
                            onChange={() => onSelect?.(getRowId(row))}
                          />
                        </TableCell>
                      )}
                      <TableCell className="text-theme-sm px-4 py-3 text-center">
                        {pagination
                          ? (pagination.page - 1) * pagination.limit +
                            rowIndex +
                            1
                          : rowIndex + 1}
                      </TableCell>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key as string}
                          className="text-theme-sm px-4 py-3 text-center"
                        >
                          {col.render
                            ? col.render(row)
                            : String(row[col.key] || "-")}
                        </TableCell>
                      ))}

                      {actions.length > 0 && (
                        <TableCell className="text-theme-sm px-2 py-3">
                          <div className="flex justify-center gap-2">
                            {actions
                              ?.filter((a) => !isActionHidden(a, row))
                              ?.map((action, index) => (
                                <div key={index} className="group relative">
                                  <TooltipHorizon
                                    extra=""
                                    placement="top"
                                    content={action.label}
                                    trigger={
                                      <button
                                        onClick={() => action.onClick(row)}
                                        className={clsx(
                                          "flex h-7 w-7 items-center justify-center rounded-md shadow-sm transition-all duration-200 hover:scale-[1.05]",
                                          getButtonClasses(action.color)
                                        )}
                                      >
                                        {getActionIcon(action)}
                                      </button>
                                    }
                                  />
                                </div>
                              ))}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
